import React from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { getPlanetShortCode, getZodiacName } from '../utils/language';

interface SudarshanChakraSvgProps {
  kundali: CompleteKundali;
  lang?: Language;
  theme?: 'deluxe' | 'eco-bw';
}

export const SudarshanChakraSvg: React.FC<SudarshanChakraSvgProps> = ({
  kundali,
  lang = 'hi',
  theme = 'deluxe'
}) => {
  const isBw = theme === 'eco-bw';
  const isHi = lang === 'hi' || lang === 'sa';

  const strokeColor = isBw ? '#000000' : '#174C4B';
  const goldColor = isBw ? '#333333' : '#B58A4A';
  const textColor = isBw ? '#000000' : '#263332';
  const primaryFill = isBw ? '#FFFFFF' : '#FFFDF7';
  const ringFill1 = isBw ? '#FFFFFF' : '#F7F3EA';
  const ringFill2 = isBw ? '#FFFFFF' : '#F2EFE7';
  const ringFill3 = isBw ? '#FFFFFF' : '#E8F0EF';

  const planets = kundali?.planets || [];
  const houses = kundali?.houses || [];

  // Determine Lagna sign number (1-12)
  const lagnaHouse = houses.find((h) => h.houseNumber === 1);
  const lagnaSignNum = (lagnaHouse as any)?.signNumber || 9; // default 9 (Sagittarius)

  // Determine Chandra sign number
  const moon = planets.find((p) => p.name === 'Moon');
  const moonSignNum = (moon as any)?.signNum || 4; // default 4 (Cancer)

  // Determine Surya sign number
  const sun = planets.find((p) => p.name === 'Sun');
  const sunSignNum = (sun as any)?.signNum || 9; // default 9 (Sagittarius)

  // Map planets to sign numbers
  const planetsBySign: Record<number, string[]> = {};
  for (let s = 1; s <= 12; s++) planetsBySign[s] = [];
  planets.forEach((p) => {
    const sNum = (p as any).signNum;
    if (sNum && planetsBySign[sNum]) {
      const code = getPlanetShortCode(p.name, lang);
      planetsBySign[sNum].push(code);
    }
  });

  const size = 500;
  const cx = 250;
  const cy = 250;

  const rOuter = 230;
  const rMiddle = 175;
  const rInner = 120;
  const rCenter = 50;

  // 12 sectors of 30 degrees each
  // Top starts at -90 degrees (12 o'clock)
  const sectors = Array.from({ length: 12 }, (_, i) => {
    const startAngle = (i * 30 - 105) * (Math.PI / 180);
    const endAngle = ((i + 1) * 30 - 105) * (Math.PI / 180);
    const midAngle = ((i + 0.5) * 30 - 105) * (Math.PI / 180);

    // House offset for each ring:
    // Outer: Surya Lagna (House 1 at top)
    const suryaSign = ((sunSignNum - 1 + i) % 12) + 1;
    // Middle: Chandra Lagna
    const chandraSign = ((moonSignNum - 1 + i) % 12) + 1;
    // Inner: Janma Lagna
    const janmaSign = ((lagnaSignNum - 1 + i) % 12) + 1;

    return {
      index: i,
      startAngle,
      endAngle,
      midAngle,
      suryaSign,
      chandraSign,
      janmaSign,
      suryaPlanets: planetsBySign[suryaSign] || [],
      chandraPlanets: planetsBySign[chandraSign] || [],
      janmaPlanets: planetsBySign[janmaSign] || []
    };
  });

  const getArcPath = (r1: number, r2: number, a1: number, a2: number) => {
    const x1 = cx + r1 * Math.cos(a1);
    const y1 = cy + r1 * Math.sin(a1);
    const x2 = cx + r2 * Math.cos(a1);
    const y2 = cy + r2 * Math.sin(a1);
    const x3 = cx + r2 * Math.cos(a2);
    const y3 = cy + r2 * Math.sin(a2);
    const x4 = cx + r1 * Math.cos(a2);
    const y4 = cy + r1 * Math.sin(a2);
    return `M ${x1} ${y1} L ${x2} ${y2} A ${r2} ${r2} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${r1} ${r1} 0 0 0 ${x1} ${y1} Z`;
  };

  return (
    <div className="w-full flex flex-col items-center justify-center p-2 text-center">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full max-w-[420px] aspect-square select-none overflow-visible"
      >
        <defs>
          <filter id="subtle-shadow" x="-5%" y="-5%" width="110%" height="110%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity={isBw ? '0' : '0.15'} />
          </filter>
        </defs>

        {/* Outer Beaded / Auspicious Rim */}
        <circle cx={cx} cy={cy} r={rOuter + 8} fill="none" stroke={goldColor} strokeWidth={isBw ? 1 : 2} />
        {Array.from({ length: 48 }).map((_, bIdx) => {
          const bAngle = (bIdx * (360 / 48)) * (Math.PI / 180);
          const bx = cx + (rOuter + 4) * Math.cos(bAngle);
          const by = cy + (rOuter + 4) * Math.sin(bAngle);
          return <circle key={bIdx} cx={bx} cy={by} r={1.5} fill={goldColor} />;
        })}

        {/* 1. OUTER RING: SURYA KUNDALI */}
        {sectors.map((s) => (
          <path
            key={`surya-${s.index}`}
            d={getArcPath(rMiddle, rOuter, s.startAngle, s.endAngle)}
            fill={s.index % 2 === 0 ? ringFill1 : primaryFill}
            stroke={strokeColor}
            strokeWidth={1}
          />
        ))}

        {/* 2. MIDDLE RING: CHANDRA KUNDALI */}
        {sectors.map((s) => (
          <path
            key={`chandra-${s.index}`}
            d={getArcPath(rInner, rMiddle, s.startAngle, s.endAngle)}
            fill={s.index % 2 === 0 ? ringFill2 : primaryFill}
            stroke={strokeColor}
            strokeWidth={1}
          />
        ))}

        {/* 3. INNER RING: JANMA LAGNA KUNDALI */}
        {sectors.map((s) => (
          <path
            key={`janma-${s.index}`}
            d={getArcPath(rCenter, rInner, s.startAngle, s.endAngle)}
            fill={s.index % 2 === 0 ? ringFill3 : primaryFill}
            stroke={strokeColor}
            strokeWidth={1}
          />
        ))}

        {/* Central Core: Sacred Swastika / Auspicious Bindu */}
        <circle cx={cx} cy={cy} r={rCenter} fill={isBw ? '#FFFFFF' : '#F7F3EA'} stroke={goldColor} strokeWidth={1.5} />
        <text
          x={cx}
          y={cy + 7}
          textAnchor="middle"
          fontSize="24"
          fontWeight="bold"
          fill={goldColor}
        >
          卐
        </text>

        {/* Sector Labels: Outer (Surya), Middle (Chandra), Inner (Janma) */}
        {sectors.map((s) => {
          // Radii for content placement
          const rOuterTxt = (rMiddle + rOuter) / 2;
          const rMidTxt = (rInner + rMiddle) / 2;
          const rInnerTxt = (rCenter + rInner) / 2;

          const ox = cx + rOuterTxt * Math.cos(s.midAngle);
          const oy = cy + rOuterTxt * Math.sin(s.midAngle);

          const mx = cx + rMidTxt * Math.cos(s.midAngle);
          const my = cy + rMidTxt * Math.sin(s.midAngle);

          const ix = cx + rInnerTxt * Math.cos(s.midAngle);
          const iy = cy + rInnerTxt * Math.sin(s.midAngle);

          return (
            <g key={`txt-${s.index}`} className="font-sans">
              {/* Outer: Sign Num & Planets */}
              <text x={ox} y={oy - 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill={textColor}>
                {s.suryaSign}
              </text>
              <text x={ox} y={oy + 7} textAnchor="middle" fontSize="8.5" fontWeight="bold" fill={isBw ? '#000000' : '#174C4B'}>
                {s.suryaPlanets.join(' ')}
              </text>

              {/* Middle: Sign Num & Planets */}
              <text x={mx} y={my - 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill={textColor}>
                {s.chandraSign}
              </text>
              <text x={mx} y={my + 7} textAnchor="middle" fontSize="8.5" fontWeight="bold" fill={isBw ? '#000000' : '#174C4B'}>
                {s.chandraPlanets.join(' ')}
              </text>

              {/* Inner: Sign Num & Planets */}
              <text x={ix} y={iy - 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill={textColor}>
                {s.janmaSign}
              </text>
              <text x={ix} y={iy + 7} textAnchor="middle" fontSize="8.5" fontWeight="bold" fill={isBw ? '#000000' : '#174C4B'}>
                {s.janmaPlanets.join(' ')}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Explanatory classical footnote below chakra */}
      <div className={`mt-3 max-w-md text-center text-[10px] italic leading-relaxed ${isBw ? 'text-[#000000]' : 'text-[#4B5956]'}`}>
        {isHi
          ? 'नोट: सुदर्शन चक्र क्रमानुसार बाहरी वृत्त से अन्तः वृत्त तक सूर्य, चन्द्र एवं जन्मांग कुंडलियों में ग्रहों की तुलनात्मक स्थिति दर्शाता है। किसी भाव का विचार करने के लिए उस भाव को प्रकट करने वाली तीनों राशियों का विचार करना चाहिए।'
          : 'Note: The Sudarshan Chakra displays the comparative planetary alignment from the outer wheel (Surya Lagna) to the middle wheel (Chandra Lagna) and inner wheel (Janma Lagna). Authentic judgment requires synthesizing all three perspectives simultaneously.'}
      </div>
    </div>
  );
};
